import React from "react";
import { FormikHelpers, Field } from "formik";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Box,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";

import { useRouteMatch } from "react-router-dom";

import { ICode } from "../types";
import { useAdminProviderDataContext } from "../../../../context";
import { FormCheckbox, FormRadioGroup } from "../../index";
import { FAILOVER_TYPE_OPTIONS } from "../utils";

interface IProps {
  values: ICode;
  isEditingDiscountCode?: boolean;
  setFieldValue: FormikHelpers<ICode>["setFieldValue"];
}
const Properties: React.FC<IProps> = ({ values, setFieldValue }) => {
  const { user } = useAdminProviderDataContext();
  const { create_active_code, set_code_expiration_props } = user.allowed_features;
  const matchCustomersPage = useRouteMatch("/customers");

  const handleChangeTypeDiscount = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFieldValue("percent", event.target.value === "percent");
  const StyledRadio = ({ ...rest }) => <Radio color="primary" {...rest} />;

  return (
    <>
      <h3>Properties</h3>

      <FormControl component="fieldset">
        <FormLabel component="legend">Discount type</FormLabel>
        <RadioGroup onChange={handleChangeTypeDiscount} value={values.percent ? "percent" : "flat"}>
          <FormControlLabel value="percent" control={<StyledRadio />} label="Percent" />
          <FormControlLabel value="flat" control={<StyledRadio />} label="Discount amount" />
        </RadioGroup>
      </FormControl>

      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={values.fixed_reset || false}
              onChange={(e) => setFieldValue("fixed_reset", e.target.checked)}
            />
          }
          label="Fixed reset"
        />
      </div>

      {values.plan !== -2 && (
        <div>
          <Field
            name="one_time"
            label="Lifetime"
            component={FormCheckbox}
            disabled={values.use_expiration}
          />
        </div>
      )}

      <FormControlLabel
        control={
          <Checkbox
            name="autoExpires"
            checked={values.autoExpires}
            onChange={(e) => {
              if (!e.target.checked) {
                setFieldValue("expires", null);
              }
              setFieldValue("autoExpires", e.target.checked);
            }}
          />
        }
        label={
          <div>
            Auto Expires{" "}
            <Tooltip
              title="Chose the date when the code no longer be available for use at checkout."
              placement="top"
            >
              <InfoIcon cursor="help" />
            </Tooltip>
          </div>
        }
      />
      {set_code_expiration_props && (
        <div>
          <FormGroup>
            <Field
              name="use_expiration"
              label={
                <div>
                  Expiration{" "}
                  <Tooltip
                    title="Set the duration for which the discount rate will be applied to a subscription."
                    placement="top"
                  >
                    <InfoIcon cursor="help" />
                  </Tooltip>
                </div>
              }
              component={FormCheckbox}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.checked) {
                  setFieldValue("one_time", true);
                }
                setFieldValue("use_expiration", e.target.checked);
                setFieldValue("use_failover_amt", e.target.checked);
                setFieldValue("use_failover_type", e.target.checked);
                setFieldValue("expiration", null);
                setFieldValue("failover_type", null);
                setFieldValue("failover_amt", null);
              }}
            />
            {values.use_expiration && (
              <Box pl="20px">
                <Field
                  name="use_failover_type"
                  label="Discount Type Past Expiration"
                  component={FormCheckbox}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setFieldValue("use_failover_amt", true);
                    } else {
                      setFieldValue("use_failover_amt", e.target.checked);
                      setFieldValue("failover_type", null);
                      setFieldValue("failover_amt", null);
                    }
                    setFieldValue("use_failover_type", e.target.checked);
                  }}
                />

                <Box pl="20px">
                  <Field
                    name="failover_type"
                    component={FormRadioGroup}
                    color="primary"
                    options={FAILOVER_TYPE_OPTIONS}
                    isDisabled={!values.use_failover_type}
                  />
                </Box>

                <Field
                  name="use_failover_amt"
                  label="Discount Amount Past Expiration"
                  component={FormCheckbox}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setFieldValue("use_failover_type", true);
                    } else {
                      setFieldValue("use_failover_type", e.target.checked);
                    }
                    setFieldValue("use_failover_amt", e.target.checked);
                  }}
                />
              </Box>
            )}
          </FormGroup>
        </div>
      )}

      <div>
        <FormControlLabel
          control={
            <Checkbox
              disabled={!create_active_code}
              checked={values.active}
              onChange={(e) => setFieldValue("active", e.target.checked)}
            />
          }
          label="Active"
        />
      </div>

      {matchCustomersPage && (
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.isReset}
                onChange={(e) => setFieldValue("isReset", e.target.checked)}
              />
            }
            label="Reset"
          />
        </div>
      )}
    </>
  );
};

export default Properties;
